import type {List} from 'immutable';

import type {ClassroomModelV1} from 'src/resources/augmented/Classroom/ClassroomModel.v1';
import getIsBaseRoute from '../isBaseRoute';
import getIsWelcomeRoute from '../isWelcomeRoute';

interface Arguments {
  classroomId: string | undefined;
  classrooms: List<ClassroomModelV1>;
  pathname: string;
}

/**
 * Determines if the user should be redirected based on their classrooms
 * and the route they are navigating to.
 *
 * The user should be redirected if any of the following conditions are met:
 *  1. They are trying to navigate to `/home` or `/home/`
 *  2. They are trying to navigate to `/home/welcome` while enrolled in at least one class
 *  3. They are trying to navigate to a class they don't have access to
 *
 * @param args - The arguments object.
 * @param args.classroomId - The ID of the classroom being navigated to, or `undefined` if none is specified.
 * @param args.classrooms - The list of classrooms the user is enrolled in.
 * @param args.pathname - The current route pathname.
 * @returns Whether the user should be redirected.
 */
export default function getShouldRedirectUser({classroomId, classrooms, pathname}: Arguments) {
  const isBaseRoute = getIsBaseRoute(pathname);
  const isWelcomeRoute = getIsWelcomeRoute(pathname);

  const hasClassrooms = classrooms.size > 0;
  // If classroomId is undefined, that means we are scoped to "All Classes", and the user
  // always belongs to "All Classes".
  const belongsToCurrentClassroom =
    classroomId === undefined || classrooms.some((classroom) => classroom.getId() === classroomId);

  const isWelcomeRouteWithClassrooms = isWelcomeRoute && hasClassrooms;
  const isNonWelcomeRouteWithoutClassrooms = !isWelcomeRoute && !hasClassrooms;

  const shouldRedirectUser =
    isBaseRoute ||
    isWelcomeRouteWithClassrooms ||
    isNonWelcomeRouteWithoutClassrooms ||
    !belongsToCurrentClassroom;

  return shouldRedirectUser;
}
